import React, { useState, useEffect } from "react";
import {
  Users, Crown, ArrowLeft, Search, Shield, Calendar,
  ChevronDown, ChevronUp, CreditCard, Clock, CheckCircle, XCircle,
  Trash2, MessageCircle, Store, Tag, AlertTriangle, Flag,
  Ban, UserX, RotateCcw, Gift, Plus, Percent, Zap, Eye, KeyRound, Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import AdminAnalyticsTab from "../components/AdminAnalyticsTab";
import AdminMealCoverageTab from "../components/AdminMealCoverageTab";
import AdminVPKpiTab from "../components/AdminVPKpiTab";
import AdminFlippPartnershipTab from "../components/AdminFlippPartnershipTab";
import { AdminScanFeedbackTab } from "../components/AdminScanFeedbackTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_COLORS = {
  trial: "bg-blue-100 text-blue-700",
  free: "bg-gray-100 text-gray-600",
  monthly: "bg-green-100 text-green-700",
  annual: "bg-purple-100 text-purple-700",
};

const AdminDashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [communityDeals, setCommunityDeals] = useState([]);
  const [reportedDeals, setReportedDeals] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedUser, setExpandedUser] = useState(null);
  const [tab, setTab] = useState("users");
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [banConfirmId, setBanConfirmId] = useState(null);
  const [deleteUserConfirmId, setDeleteUserConfirmId] = useState(null);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [promoForm, setPromoForm] = useState({ name: "", type: "trial_extension", value: "", code: "", expires_at: "", max_redemptions: "" });
  const [applyPromoModal, setApplyPromoModal] = useState(null);
  const [applyUserId, setApplyUserId] = useState("");
  const [promoRedemptions, setPromoRedemptions] = useState({});

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [usersRes, txRes, dealsRes, reportedRes, bannedRes, deletedRes, promosRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/subscription/admin/users`, { headers }),
        fetch(`${BACKEND_URL}/api/subscription/admin/transactions`, { headers }),
        fetch(`${BACKEND_URL}/api/community/admin/deals`, { headers }),
        fetch(`${BACKEND_URL}/api/community/admin/reported-deals`, { headers }),
        fetch(`${BACKEND_URL}/api/subscription/admin/banned`, { headers }),
        fetch(`${BACKEND_URL}/api/subscription/admin/deleted`, { headers }),
        fetch(`${BACKEND_URL}/api/subscription/admin/promotions`, { headers }),
      ]);
      if (usersRes.ok) { const d = await usersRes.json(); setUsers(d.users || []); }
      if (txRes.ok) { const d = await txRes.json(); setTransactions(d.transactions || []); }
      if (dealsRes.ok) { const d = await dealsRes.json(); setCommunityDeals(d.deals || []); }
      if (reportedRes.ok) { const d = await reportedRes.json(); setReportedDeals(d.deals || []); }
      if (bannedRes.ok) { const d = await bannedRes.json(); setBannedUsers(d.users || []); }
      if (deletedRes.ok) { const d = await deletedRes.json(); setDeletedUsers(d.users || []); }
      if (promosRes.ok) { const d = await promosRes.json(); setPromotions(d.promotions || []); }
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const updateUser = async (userId, body) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/admin/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) await loadData();
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const extendTrial = (userId) => {
    const newEnd = new Date();
    newEnd.setDate(newEnd.getDate() + 14);
    updateUser(userId, { subscription: { plan: "trial", status: "active", trial_end: newEnd.toISOString() } });
  };

  const cancelSub = (userId) => {
    updateUser(userId, { subscription: { plan: "free", status: "cancelled" } });
  };

  const deleteAdminDeal = async (dealId) => {
    setActionLoading(dealId);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/community/deals/${dealId}?admin_email=${encodeURIComponent(user?.email || "marvin.wade@mail.com")}`,
        { method: "DELETE" }
      );
      if (res.ok) {
        setCommunityDeals(prev => prev.filter(d => d.id !== dealId));
        setReportedDeals(prev => prev.filter(d => d.id !== dealId));
        setDeleteConfirmId(null);
      }
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const banUser = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/admin/users/${userId}/ban`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: "Flagged by admin" }),
      });
      if (res.ok) { await loadData(); setBanConfirmId(null); }
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const unbanUser = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/admin/users/${userId}/unban`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) await loadData();
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const softDeleteUser = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { await loadData(); setDeleteUserConfirmId(null); }
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const restoreUser = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/admin/users/${userId}/restore`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) await loadData();
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const sendResetLink = async (userId) => {
    setActionLoading(`reset-${userId}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/admin-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Reset code sent to ${data.user_email}`);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || "Failed to send reset link");
      }
    } catch (e) { alert("Error sending reset link"); }
    setActionLoading(null);
  };

  const createPromotion = async () => {
    setActionLoading("create-promo");
    try {
      const body = {
        name: promoForm.name,
        type: promoForm.type,
        value: parseFloat(promoForm.value),
        code: promoForm.code || null,
        expires_at: promoForm.expires_at || null,
        max_redemptions: promoForm.max_redemptions ? parseInt(promoForm.max_redemptions) : null,
      };
      const res = await fetch(`${BACKEND_URL}/api/subscription/admin/promotions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setShowCreatePromo(false);
        setPromoForm({ name: "", type: "trial_extension", value: "", code: "", expires_at: "", max_redemptions: "" });
        await loadData();
      }
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const togglePromoStatus = async (promoId, currentStatus) => {
    setActionLoading(promoId);
    const action = currentStatus === "active" ? "deactivate" : "activate";
    try {
      await fetch(`${BACKEND_URL}/api/subscription/admin/promotions/${promoId}/${action}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadData();
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const deletePromotion = async (promoId) => {
    setActionLoading(promoId);
    try {
      await fetch(`${BACKEND_URL}/api/subscription/admin/promotions/${promoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadData();
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const applyPromoToUser = async (promoId) => {
    if (!applyUserId) return;
    setActionLoading(promoId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/admin/promotions/${promoId}/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: applyUserId }),
      });
      if (res.ok) {
        setApplyPromoModal(null);
        setApplyUserId("");
        await loadData();
      }
    } catch (e) { /* ignore */ }
    setActionLoading(null);
  };

  const viewRedemptions = async (promoId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/admin/promotions/${promoId}/redemptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setPromoRedemptions(prev => ({ ...prev, [promoId]: d.redemptions || [] }));
      }
    } catch (e) { /* ignore */ }
  };

  const grantPremium = (userId, plan) => {
    // Admin-granted subscriptions are marked granted_by="admin" on the backend
    // so analytics can treat them as $0 revenue (effectively free users).
    updateUser(userId, { subscription: { plan, status: "active" } });
  };

  const isGranted = (u) =>
    (u.subscription?.granted_by === "admin") ||
    (typeof u.subscription?.granted_by === "string" && u.subscription.granted_by.startsWith("promo:"));

  const filteredUsers = users.filter(u =>
    (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: users.length,
    trial: users.filter(u => u.subscription?.plan === "trial").length,
    // Paid: real monthly/annual subscribers NOT admin-granted / promo-granted
    paid: users.filter(u =>
      ["monthly", "annual"].includes(u.subscription?.plan) && !isGranted(u)
    ).length,
    // Free: anyone with plan="free", plan missing, or granted (we earn $0)
    free: users.filter(u => {
      const plan = u.subscription?.plan;
      if (plan === "trial") return false;
      if (!plan || plan === "free") return true;
      if (["monthly", "annual"].includes(plan) && isGranted(u)) return true;
      return false;
    }).length,
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[50vh]">
        <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-100 rounded-full" data-testid="admin-back-btn">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-600" /> Admin Dashboard
          </h2>
          <p className="text-sm text-gray-500">Manage users & subscriptions</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "Total", value: stats.total, color: "text-gray-900" },
          { label: "Trial", value: stats.trial, color: "text-blue-600" },
          { label: "Paid", value: stats.paid, color: "text-green-600" },
          { label: "Free", value: stats.free, color: "text-gray-500" },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-3 text-center">
              <p className={`text-lg font-bold ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>{s.value}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { key: "analytics", label: "Analytics" },
          { key: "vp-kpi", label: "VP KPIs" },
          { key: "flipp", label: "Flipp Partnership" },
          { key: "meal-coverage", label: "Meal Coverage" },
          { key: "users", label: "Users" },
          { key: "promotions", label: `Promos (${promotions.length})` },
          { key: "deals", label: `Deals (${communityDeals.length})` },
          { key: "reported", label: `Reported (${reportedDeals.length})` },
          { key: "banned", label: `Banned (${bannedUsers.length})` },
          { key: "deleted", label: `Deleted (${deletedUsers.length})` },
          { key: "transactions", label: "Transactions" },
          { key: "scan-feedback", label: "Scan Feedback" },
        ].map(t => (
          <button
            key={t.key}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "analytics" && <AdminAnalyticsTab />}
      {tab === "scan-feedback" && <AdminScanFeedbackTab />}

      {tab === "vp-kpi" && <AdminVPKpiTab />}

      {tab === "flipp" && <AdminFlippPartnershipTab />}

      {tab === "meal-coverage" && <AdminMealCoverageTab />}

      {tab === "users" && (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="admin-search"
            />
          </div>

          {/* User List */}
          <div className="space-y-2">
            {filteredUsers.map(u => {
              const plan = u.subscription?.plan || "free";
              const granted = isGranted(u);
              // Granted monthly/annual render as gray "free (granted)" so the
              // admin sees at a glance that they contribute $0 revenue.
              const displayPlan = granted && ["monthly", "annual"].includes(plan)
                ? "free (granted)"
                : plan;
              const badgeClass = granted && ["monthly", "annual"].includes(plan)
                ? PLAN_COLORS.free
                : (PLAN_COLORS[plan] || PLAN_COLORS.free);
              const isExpanded = expandedUser === u.id;
              return (
                <Card key={u.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    <button
                      className="w-full p-3 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedUser(isExpanded ? null : u.id)}
                      data-testid={`user-row-${u.id}`}
                    >
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover bg-gray-100" />
                      ) : (
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{u.name || "—"}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                      <Badge className={`text-[10px] ${badgeClass}`} data-testid={`user-plan-badge-${u.id}`}>{displayPlan}</Badge>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                    </button>

                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-3 border-t bg-gray-50">
                        {/* Details */}
                        <div className="grid grid-cols-2 gap-2 pt-3 text-xs">
                          <div>
                            <span className="text-gray-500">Role:</span>{" "}
                            <span className="font-medium capitalize">{u.role || "user"}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Joined:</span>{" "}
                            <span className="font-medium">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>{" "}
                            <span className="font-medium truncate">{u.email || "—"}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Phone:</span>{" "}
                            <span className="font-medium">{u.phone_number || "—"}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Location:</span>{" "}
                            <span className="font-medium">{u.city && u.state ? `${u.city}, ${u.state}` : u.zip_code || "—"}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Profile:</span>{" "}
                            <span className="font-medium">{u.profile_completed ? "Complete" : "Incomplete"}</span>
                          </div>
                          {plan === "trial" && u.subscription?.trial_end && (
                            <div className="col-span-2">
                              <span className="text-gray-500">Trial ends:</span>{" "}
                              <span className="font-medium">{new Date(u.subscription.trial_end).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => extendTrial(u.id)}
                            disabled={actionLoading === u.id}
                            data-testid={`extend-trial-${u.id}`}
                          >
                            <Clock className="h-3 w-3 mr-1" /> Extend Trial 14d
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7 text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => grantPremium(u.id, "monthly")}
                            disabled={actionLoading === u.id}
                            data-testid={`grant-monthly-${u.id}`}
                          >
                            <Crown className="h-3 w-3 mr-1" /> Grant Monthly
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7 text-purple-600 border-purple-200 hover:bg-purple-50"
                            onClick={() => grantPremium(u.id, "annual")}
                            disabled={actionLoading === u.id}
                            data-testid={`grant-annual-${u.id}`}
                          >
                            <Crown className="h-3 w-3 mr-1" /> Grant Annual
                          </Button>
                          {["monthly", "annual"].includes(plan) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => cancelSub(u.id)}
                              disabled={actionLoading === u.id}
                              data-testid={`cancel-sub-${u.id}`}
                            >
                              <XCircle className="h-3 w-3 mr-1" /> Cancel Sub
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7 text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => sendResetLink(u.id)}
                            disabled={actionLoading === `reset-${u.id}`}
                            data-testid={`reset-pin-${u.id}`}
                          >
                            <KeyRound className="h-3 w-3 mr-1" />
                            {actionLoading === `reset-${u.id}` ? "Sending..." : "Send Reset Link"}
                          </Button>
                        </div>
                        {/* Ban & Delete Actions */}
                        {u.email !== "marvin.wade@mail.com" && (
                          <div className="flex gap-2 pt-1 border-t mt-2">
                            {banConfirmId === u.id ? (
                              <div className="flex gap-1 flex-1">
                                <Button size="sm" variant="outline" className="text-xs h-7 flex-1" onClick={() => setBanConfirmId(null)}>Cancel</Button>
                                <Button size="sm" className="text-xs h-7 flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => banUser(u.id)} disabled={actionLoading === u.id} data-testid={`confirm-ban-${u.id}`}>
                                  <Ban className="h-3 w-3 mr-1" />Confirm Ban
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline" className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setBanConfirmId(u.id)} data-testid={`ban-user-${u.id}`}>
                                <Ban className="h-3 w-3 mr-1" />Ban User
                              </Button>
                            )}
                            {deleteUserConfirmId === u.id ? (
                              <div className="flex gap-1 flex-1">
                                <Button size="sm" variant="outline" className="text-xs h-7 flex-1" onClick={() => setDeleteUserConfirmId(null)}>Cancel</Button>
                                <Button size="sm" className="text-xs h-7 flex-1 bg-gray-700 hover:bg-gray-800 text-white" onClick={() => softDeleteUser(u.id)} disabled={actionLoading === u.id} data-testid={`confirm-delete-user-${u.id}`}>
                                  <UserX className="h-3 w-3 mr-1" />Confirm Delete
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline" className="text-xs h-7 text-gray-600 border-gray-200 hover:bg-gray-50" onClick={() => setDeleteUserConfirmId(u.id)} data-testid={`delete-user-${u.id}`}>
                                <UserX className="h-3 w-3 mr-1" />Delete Account
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {filteredUsers.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">No users found</p>
            )}
          </div>
        </>
      )}

      {tab === "promotions" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">Manage promotions, promo codes, and deals</p>
            <Button size="sm" className="text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => setShowCreatePromo(!showCreatePromo)} data-testid="create-promo-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />{showCreatePromo ? "Cancel" : "New Promotion"}
            </Button>
          </div>

          {/* Create Promotion Form */}
          {showCreatePromo && (
            <Card className="border-green-200">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2"><Gift className="h-4 w-4 text-green-600" />Create Promotion</h3>
                <Input placeholder="Promotion name (e.g. Spring Sale)" value={promoForm.name} onChange={e => setPromoForm(p => ({ ...p, name: e.target.value }))} className="text-sm" data-testid="promo-name-input" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Type</label>
                    <select value={promoForm.type} onChange={e => setPromoForm(p => ({ ...p, type: e.target.value }))} className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="promo-type-select">
                      <option value="trial_extension">Trial Extension</option>
                      <option value="subscription_discount">Subscription Discount</option>
                      <option value="free_premium">Free Premium Access</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      {promoForm.type === "subscription_discount" ? "Discount (%)" : "Days"}
                    </label>
                    <Input type="number" placeholder={promoForm.type === "subscription_discount" ? "e.g. 50" : "e.g. 30"} value={promoForm.value} onChange={e => setPromoForm(p => ({ ...p, value: e.target.value }))} className="text-sm" data-testid="promo-value-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Promo Code (optional)</label>
                    <Input placeholder="e.g. SPRING50" value={promoForm.code} onChange={e => setPromoForm(p => ({ ...p, code: e.target.value }))} className="text-sm uppercase" data-testid="promo-code-input" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Max Uses (optional)</label>
                    <Input type="number" placeholder="Unlimited" value={promoForm.max_redemptions} onChange={e => setPromoForm(p => ({ ...p, max_redemptions: e.target.value }))} className="text-sm" data-testid="promo-max-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Expiration Date (optional)</label>
                  <Input type="datetime-local" value={promoForm.expires_at} onChange={e => setPromoForm(p => ({ ...p, expires_at: e.target.value }))} className="text-sm" data-testid="promo-expires-input" />
                </div>
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white" onClick={createPromotion} disabled={!promoForm.name || !promoForm.value || actionLoading === "create-promo"} data-testid="save-promo-btn">
                  <CheckCircle className="h-4 w-4 mr-1" />Create Promotion
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Promotions List */}
          {promotions.length === 0 && !showCreatePromo ? (
            <p className="text-sm text-gray-400 text-center py-8">No promotions created yet</p>
          ) : (
            promotions.map(promo => {
              const status = promo.computed_status || promo.status;
              const typeIcon = promo.type === "trial_extension" ? <Clock className="h-4 w-4 text-blue-500" /> : promo.type === "subscription_discount" ? <Percent className="h-4 w-4 text-green-500" /> : <Zap className="h-4 w-4 text-purple-500" />;
              const typeLabel = promo.type === "trial_extension" ? "Trial Extension" : promo.type === "subscription_discount" ? "Subscription Discount" : "Free Premium";
              const statusColor = status === "active" ? "bg-green-100 text-green-700" : status === "deactivated" ? "bg-gray-100 text-gray-600" : status === "expired" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700";
              const redemptions = promoRedemptions[promo.id];

              return (
                <Card key={promo.id} className={`overflow-hidden ${status !== "active" ? "opacity-70" : ""}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {typeIcon}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{promo.name}</p>
                          <p className="text-xs text-gray-500">{typeLabel} — {promo.type === "subscription_discount" ? `${promo.value}% off` : `${promo.value} days`}</p>
                        </div>
                      </div>
                      <Badge className={`text-xs ${statusColor}`}>{status}</Badge>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                      {promo.code && (
                        <span className="bg-gray-100 px-2 py-0.5 rounded font-mono font-bold text-gray-700">{promo.code}</span>
                      )}
                      <span>{promo.redemption_count || 0} redeemed{promo.max_redemptions ? ` / ${promo.max_redemptions} max` : ""}</span>
                      {promo.expires_at && <span>Exp: {new Date(promo.expires_at).toLocaleDateString()}</span>}
                      {!promo.expires_at && <span>No expiry</span>}
                    </div>

                    <div className="flex gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="text-xs h-6 px-2" onClick={() => { viewRedemptions(promo.id); setApplyPromoModal(applyPromoModal === promo.id ? null : promo.id); }} data-testid={`promo-details-${promo.id}`}>
                        <Eye className="h-3 w-3 mr-1" />{applyPromoModal === promo.id ? "Close" : "Details"}
                      </Button>
                      {status === "active" && (
                        <Button size="sm" variant="outline" className="text-xs h-6 px-2 text-amber-600 border-amber-200" onClick={() => togglePromoStatus(promo.id, "active")} disabled={actionLoading === promo.id}>
                          <XCircle className="h-3 w-3 mr-1" />Deactivate
                        </Button>
                      )}
                      {status === "deactivated" && (
                        <Button size="sm" variant="outline" className="text-xs h-6 px-2 text-green-600 border-green-200" onClick={() => togglePromoStatus(promo.id, "deactivated")} disabled={actionLoading === promo.id}>
                          <CheckCircle className="h-3 w-3 mr-1" />Reactivate
                        </Button>
                      )}
                      {deleteConfirmId === promo.id ? (
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="text-xs h-6 px-2" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                          <Button size="sm" className="text-xs h-6 px-2 bg-red-600 hover:bg-red-700 text-white" onClick={() => { deletePromotion(promo.id); setDeleteConfirmId(null); }}>
                            <Trash2 className="h-3 w-3 mr-1" />Confirm
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" className="text-xs h-6 px-2 text-red-500 border-red-200" onClick={() => setDeleteConfirmId(promo.id)}>
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                      )}
                    </div>

                    {/* Apply to User / View Redemptions */}
                    {applyPromoModal === promo.id && (
                      <div className="border-t pt-2 space-y-2">
                        {status === "active" && (
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1">Apply to User</p>
                            <div className="flex gap-1.5">
                              <select value={applyUserId} onChange={e => setApplyUserId(e.target.value)} className="flex-1 border rounded-lg px-2 py-1 text-xs bg-white" data-testid="apply-user-select">
                                <option value="">Select user...</option>
                                {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                              </select>
                              <Button size="sm" className="text-xs h-7 bg-green-600 hover:bg-green-700 text-white" onClick={() => applyPromoToUser(promo.id)} disabled={!applyUserId || actionLoading === promo.id} data-testid="apply-promo-btn">
                                <Gift className="h-3 w-3 mr-1" />Apply
                              </Button>
                            </div>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">Redemptions ({redemptions?.length || 0})</p>
                          {redemptions && redemptions.length > 0 ? (
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {redemptions.map(r => (
                                <div key={r.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                                  <span className="text-gray-700">{r.user_name || r.user_email}</span>
                                  <span className="text-gray-400">{new Date(r.redeemed_at).toLocaleDateString()}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400">No redemptions yet</p>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {tab === "deals" && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">Remove inappropriate or misleading community posts</p>
          {communityDeals.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No community deals posted yet</p>
          ) : (
            communityDeals.map(deal => (
              <Card key={deal.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    {deal.image_url && (
                      <img src={deal.image_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0 bg-gray-100" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900 line-clamp-1">{deal.title}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            <span className="flex items-center gap-0.5"><Store className="h-3 w-3" />{deal.store || "—"}</span>
                            <span>by {deal.posted_by || deal.username || "Anonymous"}</span>
                          </div>
                        </div>
                        {deal.price && (
                          <Badge className="bg-green-100 text-green-700 text-xs flex-shrink-0">${deal.price}</Badge>
                        )}
                      </div>
                      {deal.description && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{deal.description}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span>{deal.likes || 0} likes</span>
                          <span>{deal.comments_count || 0} comments</span>
                          <span>{deal.created_at ? new Date(deal.created_at).toLocaleDateString() : "—"}</span>
                        </div>
                        {deleteConfirmId === deal.id ? (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" className="text-xs h-6 px-2" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                            <Button
                              size="sm"
                              className="text-xs h-6 px-2 bg-red-600 hover:bg-red-700 text-white"
                              onClick={() => deleteAdminDeal(deal.id)}
                              disabled={actionLoading === deal.id}
                              data-testid={`admin-delete-confirm-${deal.id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />Remove
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-6 px-2 text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => setDeleteConfirmId(deal.id)}
                            data-testid={`admin-delete-${deal.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {tab === "reported" && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">Deals flagged by users for review</p>
          {reportedDeals.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No reported deals</p>
          ) : (
            reportedDeals.map(deal => (
              <Card key={deal.id} className="overflow-hidden border-amber-200">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Flag className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900 line-clamp-1">{deal.title}</p>
                          <p className="text-xs text-gray-500">by {deal.posted_by} &bull; {deal.store || "—"}</p>
                        </div>
                        <Badge className="bg-amber-100 text-amber-700 text-xs flex-shrink-0">
                          <Flag className="h-3 w-3 mr-0.5" />{deal.report_count} report{deal.report_count > 1 ? "s" : ""}
                        </Badge>
                      </div>
                      {deal.reports && deal.reports.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {deal.reports.map((r, i) => (
                            <div key={i} className="text-xs bg-gray-50 rounded px-2 py-1 flex items-center gap-2">
                              <span className="text-gray-500">{r.reporter_name}:</span>
                              <span className="text-gray-700 font-medium">{r.reason}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-end mt-2">
                        {deleteConfirmId === deal.id ? (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" className="text-xs h-6 px-2" onClick={() => setDeleteConfirmId(null)}>Keep</Button>
                            <Button size="sm" className="text-xs h-6 px-2 bg-red-600 hover:bg-red-700 text-white" onClick={() => deleteAdminDeal(deal.id)} disabled={actionLoading === deal.id} data-testid={`admin-remove-reported-${deal.id}`}>
                              <Trash2 className="h-3 w-3 mr-1" />Remove Deal
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="text-xs h-6 px-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteConfirmId(deal.id)}>
                            <Trash2 className="h-3 w-3 mr-1" />Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {tab === "banned" && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">Users banned for fraudulent activity</p>
          {bannedUsers.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No banned users</p>
          ) : (
            bannedUsers.map(u => (
              <Card key={u.id} className="overflow-hidden border-red-200">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <Ban className="h-4 w-4 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{u.name || "—"}</p>
                    <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    {u.ban_reason && <p className="text-xs text-red-500 mt-0.5">Reason: {u.ban_reason}</p>}
                    {u.banned_at && <p className="text-[10px] text-gray-400">Banned: {new Date(u.banned_at).toLocaleDateString()}</p>}
                  </div>
                  <Button size="sm" variant="outline" className="text-xs h-7 text-green-600 border-green-200 hover:bg-green-50" onClick={() => unbanUser(u.id)} disabled={actionLoading === u.id} data-testid={`unban-${u.id}`}>
                    <RotateCcw className="h-3 w-3 mr-1" />Unban
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {tab === "deleted" && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">Accounts removed from the active user list</p>
          {deletedUsers.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No deleted accounts</p>
          ) : (
            deletedUsers.map(u => (
              <Card key={u.id} className="overflow-hidden border-gray-300">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <UserX className="h-4 w-4 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{u.name || "—"}</p>
                    <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    {u.deleted_at && <p className="text-[10px] text-gray-400">Deleted: {new Date(u.deleted_at).toLocaleDateString()}</p>}
                  </div>
                  <Button size="sm" variant="outline" className="text-xs h-7 text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => restoreUser(u.id)} disabled={actionLoading === u.id} data-testid={`restore-${u.id}`}>
                    <RotateCcw className="h-3 w-3 mr-1" />Restore
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {tab === "transactions" && (
        <div className="space-y-2">
          {transactions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No transactions yet</p>
          ) : (
            transactions.map((tx, i) => (
              <Card key={i}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    tx.payment_status === "paid" ? "bg-green-100" : "bg-amber-100"
                  }`}>
                    {tx.payment_status === "paid" ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <CreditCard className="h-4 w-4 text-amber-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{tx.email || "—"}</p>
                    <p className="text-xs text-gray-500">{tx.plan} - ${tx.amount} {tx.currency?.toUpperCase()}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={tx.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                      {tx.payment_status}
                    </Badge>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : "—"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
